import { useCallback, useMemo } from 'react'
import type { TContextMenuItem } from '@/client/components/Layout/LayoutManager'
import type { ContextTarget } from '@/client/utils/useContextMenu'
import type { TFunction } from 'i18next'
import type { TTabInput } from '@client/stores/tabsStore'
import { createDefaultListContextMenuItems } from '@/client/utils/listContextMenuDefaults'

export default function useListContextMenuItems<TItem>(opts: {
    t: TFunction
    // LayoutManager wiring
    onContextMenu: (
        event: React.MouseEvent,
        contextMenuItems: TContextMenuItem[],
        currentElement: { id: string; label: string }
    ) => void
    addTab: (tab: TTabInput) => void
    openTabToRight: (tab: TTabInput) => void
    removeTabCopies: (sourceTabId: string) => void
    removeOtherTabs: (sourceTabId: string) => void

    // Item mapping
    getItemById: (id: string) => TItem | null
    createTab: (item: TItem) => TTabInput

    // Extra menu items (optional)
    extraItems?: TContextMenuItem[] | ((t: TFunction) => TContextMenuItem[])
    includeSeparatorBeforeExtra?: boolean
}) {
    const {
        t,
        onContextMenu,
        addTab,
        openTabToRight,
        removeTabCopies,
        removeOtherTabs,
        getItemById,
        createTab,
        extraItems,
        includeSeparatorBeforeExtra = true,
    } = opts

    const baseItems = useMemo<TContextMenuItem[]>(
        () =>
            createDefaultListContextMenuItems<TItem>({
                t,
                getItemById,
                addTab,
                openTabToRight,
                createTab,
                removeTabCopies,
                removeOtherTabs,
            }),
        [t, getItemById, addTab, openTabToRight, createTab, removeTabCopies, removeOtherTabs]
    )

    const extras = useMemo<TContextMenuItem[]>(() => {
        if (!extraItems) return []
        return typeof extraItems === 'function' ? extraItems(t) : extraItems
    }, [extraItems, t])

    const items = useMemo<TContextMenuItem[]>(() => {
        if (!extras.length) return baseItems
        return includeSeparatorBeforeExtra ? [...baseItems, { separator: true }, ...extras] : [...baseItems, ...extras]
    }, [baseItems, extras, includeSeparatorBeforeExtra])

    const show = useCallback(
        (event: React.MouseEvent, target: ContextTarget) => {
            onContextMenu(event, items, { id: target.id, label: target.label ?? '' })
        },
        [onContextMenu, items]
    )

    return { items, show }
}
